import { useState, useEffect, useRef } from "react";
import SearchIcon from "../../components/assets/searchIcon.svg";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { CircularProgress } from "@mui/material";
import { ReportServices } from "../../../src/components/services/ReportServices";
import { handleApiErrorResponses } from "../../components/utils/ApisErrorHandling";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import HospitalCustomPaginaion from "../utils/HospitalPaginationUtil";
import downArrow from "../../components/assets/down_arrow.svg";
import searchIcon from "../../components/assets/search-icon.svg";
import UpsideDownArrowIcon from "../../components/assets/up-down-arrow.svg";
import downloadIcon from "../../components/assets/down-arrow.svg";
import { HospitalServices } from "../../components/services/HospitalServices";
import { toast } from "react-toastify";

const CcMonthlyCaseReport = () => {
  const [reportData, setReportData] = useState([]);
  const [filteredReportData, setFilteredReportData] = useState([]);
  const [paginatedData, setPaginatedData] = useState([]);

  const [stageType, setStageType] = useState("all");
  const [caseType, setCaseType] = useState("");
  const [caseStage, setCaseStage] = useState("");
  const [subStageType, setSubStageType] = useState("");

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(10);
  const [value, setValue] = useState(10);

  // Date states for native date inputs
  const [startDateInput, setStartDateInput] = useState("");
  const [endDateInput, setEndDateInput] = useState("");

  // Internal range for API
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const [loading, setLoading] = useState(false);
  const [showDownloadOptions, setShowDownloadOptions] = useState(false);
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [searchKey, setSearchKey] = useState("");

  const [hospitalList, setHospitalList] = useState([]);
  const [selectedHospitalId, setSelectedHospitalId] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");

  const searchTimeRef = useRef();

  const handleSearch = (e) => {
    if (searchTimeRef.current) clearInterval(searchTimeRef.current);
    searchTimeRef.current = setTimeout(() => setSearchKey(e.target.value), 500);
    setPage(1);
  };

  const setMonthRange = (year, month) => {
    if (!year || !month) {
      setStartDate(null);
      setEndDate(null);
      return;
    }

    const y = Number(year);
    const m = Number(month) - 1;

    const start = new Date(Date.UTC(y, m, 1, 0, 0, 0, 0));
    const end = new Date(Date.UTC(y, m + 1, 0, 23, 59, 59, 999));

    setStartDate(start.toISOString());
    setEndDate(end.toISOString());
  };

  const getMonthlyReport = async () => {
    if (!startDate || !endDate || !selectedHospitalId) return;

    setLoading(true);
    try {
      const res = await ReportServices?.getHsaMonthlyReport(
        startDate,
        endDate,
        selectedHospitalId,
        "", // no filterStage from CC (caseStage is client-side)
      );

      const data = res?.data?.monthlyCaseReport || [];
      setReportData(data);
      setFilteredReportData(data);
      setPage(1);

      const startIndex = 0;
      const sliced = data.slice(startIndex, startIndex + limit);
      setPaginatedData(sliced);
    } catch (error) {
      handleApiErrorResponses(error);
    }
    setLoading(false);
  };

  const generatePdf = (caseData) => {
    const doc = new jsPDF("landscape");

    const formatDateTime = (date) =>
      date
        ? new Date(date).toLocaleString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: false,
          })
        : "NA";

    const formatDateOnly = (date) =>
      date
        ? new Date(date).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          })
        : "NA";

    const headers = [
      "SNo.",
      "Patient Name",
      "Beneficiary ID",
      "Registered ID",
      "Gender/Age",
      "Registered Date",
      "Admission Date",
    ];

    const rows = caseData.map((item, index) => [
      index + 1,
      item.patientName || "NA",
      item.beneficiaryNo || "NA",
      item.pmjayRegistrationId || "NA",
      `${item.gender || "NA"}/${item.age || "NA"}`,
      formatDateTime(item.registeredDate),
      formatDateOnly(item.admissionDate),
      {
        content: item.currenthsaStage || "NA",
        colSpan: 2,
        styles: { halign: "center" },
      },
    ]);

    autoTable(doc, {
      head: [[...headers, "Current Case Stage", ""]],
      body: rows,
      styles: { fontSize: 8, cellPadding: 3 },
      headStyles: {
        fillColor: [240, 240, 240],
        textColor: [0, 0, 0],
      },
      startY: 20,
    });

    doc.save("cc_monthly_case_report.pdf");
  };

  const generateExcel = (caseData) => {
    if (!caseData?.length) {
      toast.info("No data present to generate excel.");
      return;
    }

    setDownloading(true);

    try {
      const formatDateTime = (date) =>
        date
          ? new Date(date).toLocaleString("en-GB", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
              hour12: false,
            })
          : "NA";

      const formatDateOnly = (date) =>
        date
          ? new Date(date).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
            })
          : "NA";

      const excelData = caseData.map((item, index) => ({
        "SNo.": index + 1,
        "Patient Name": item?.patientName || "NA",
        "Beneficiary ID": item?.beneficiaryNo || "NA",
        "Registered ID": item?.pmjayRegistrationId || "NA",
        "Gender/Age": `${item?.gender || "NA"}/${item?.age || "NA"}`,
        "Registered Date": formatDateTime(item?.registeredDate),
        "Admission Date": formatDateOnly(item?.admissionDate),
        "Current Case Stage": item?.currenthsaStage || "NA",
      }));

      const worksheet = XLSX.utils.json_to_sheet(excelData);

      worksheet["!cols"] = [
        { wch: 8 },
        { wch: 25 },
        { wch: 22 },
        { wch: 22 },
        { wch: 15 },
        { wch: 25 },
        { wch: 18 },
        { wch: 30 },
      ];

      const workbook = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(workbook, worksheet, "Monthly Case Report");

      XLSX.writeFile(
        workbook,
        `CC_Monthly_Case_Report_${selectedYear}_${selectedMonth}.xlsx`,
      );
    } catch (error) {
      handleApiErrorResponses(error);
    } finally {
      setDownloading(false);
    }
  };

  const months = [
    { value: "01", label: "January" },
    { value: "02", label: "February" },
    { value: "03", label: "March" },
    { value: "04", label: "April" },
    { value: "05", label: "May" },
    { value: "06", label: "June" },
    { value: "07", label: "July" },
    { value: "08", label: "August" },
    { value: "09", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" },
  ];

  // Convert date input values to API format (YYYY-MM-DDTHH:mm:ss.sssZ)
  useEffect(() => {
    if (!startDateInput || !endDateInput) {
      setStartDate(null);
      setEndDate(null);
      return;
    }

    // Start of startDate (00:00:00.000)
    const start = new Date(startDateInput + "T00:00:00.000Z");
    // End of endDate (23:59:59.999)
    const end = new Date(endDateInput + "T23:59:59.999Z");

    setStartDate(start.toISOString());
    setEndDate(end.toISOString());
  }, [startDateInput, endDateInput]);

  // refetch when range or hospital changes
  useEffect(() => {
    if (!startDate || !endDate || !selectedHospitalId) return;
    getMonthlyReport();
  }, [startDate, endDate, selectedHospitalId]);

  useEffect(() => {
    const getHospitalList = async () => {
      try {
        const response = await HospitalServices.gethospitals("", 1, 1500);
        setHospitalList(response.data.list || []);
      } catch (error) {
        handleApiErrorResponses(error);
      }
    };

    getHospitalList();
  }, []);

  // ALL OTHER FILTERING useEffects remain EXACTLY THE SAME (unchanged)
  useEffect(() => {
    if (!stageType || stageType === "all") {
      const startIndex = 0;
      setFilteredReportData(reportData);
      const sliced = reportData.slice(startIndex, startIndex + limit);
      setPaginatedData(sliced);
      return;
    }

    const modifiedData = reportData?.filter((data) =>
      data?.currenthsaStage?.toLowerCase()?.includes(stageType?.toLowerCase()),
    );
    setFilteredReportData(modifiedData);
    setPage(1);
    const startIndex = 0;
    const sliced = modifiedData.slice(startIndex, startIndex + limit);
    setPaginatedData(sliced);
  }, [stageType, reportData, limit]);

  useEffect(() => {
    if (!caseType || caseType === "all") {
      const startIndex = 0;
      setFilteredReportData(reportData);
      const sliced = reportData.slice(startIndex, startIndex + limit);
      setPaginatedData(sliced);
      return;
    }

    const modifiedData = reportData?.filter((data) =>
      data?.caseType?.toLowerCase()?.includes(caseType?.toLowerCase()),
    );
    setFilteredReportData(modifiedData);
    setPage(1);
    const startIndex = 0;
    const sliced = modifiedData.slice(startIndex, startIndex + limit);
    setPaginatedData(sliced);
  }, [caseType, reportData, limit]);

  useEffect(() => {
    if (!caseStage || caseStage === "all") {
      const startIndex = 0;
      setFilteredReportData(reportData);
      const sliced = reportData.slice(startIndex, startIndex + limit);
      setPaginatedData(sliced);
      return;
    }

    const modifiedData = reportData?.filter((data) =>
      data?.currenthsaStage?.toLowerCase()?.includes(caseStage?.toLowerCase()),
    );
    setFilteredReportData(modifiedData);
    setPage(1);
    const startIndex = 0;
    const sliced = modifiedData.slice(startIndex, startIndex + limit);
    setPaginatedData(sliced);
  }, [caseStage, reportData, limit]);

  useEffect(() => {
    if (!searchKey) {
      const startIndex = 0;
      setFilteredReportData(reportData);
      const sliced = reportData.slice(startIndex, startIndex + limit);
      setPaginatedData(sliced);
      return;
    }

    const modifiedData1 = reportData?.filter((data) =>
      data?.patientName?.toLowerCase()?.includes(searchKey?.toLowerCase()),
    );
    const modifiedData2 = reportData?.filter((data) =>
      data?.pmjayRegistrationId
        ?.toLowerCase()
        ?.includes(searchKey?.toLowerCase()),
    );
    const modifiedData3 = reportData?.filter((data) =>
      data?.beneficiaryNo?.toLowerCase()?.includes(searchKey?.toLowerCase()),
    );

    const merged = [...modifiedData1, ...modifiedData2, ...modifiedData3];
    const unique = Array.from(
      new Map(merged.map((item) => [item.pmjayRegistrationId, item])).values(),
    );

    setFilteredReportData(unique);
    setPage(1);
    const startIndex = 0;
    const sliced = unique.slice(startIndex, startIndex + limit);
    setPaginatedData(sliced);
  }, [searchKey, reportData, limit]);

  useEffect(() => {
    const totalItems = filteredReportData?.length || 0;
    setTotalPages(Math.ceil(totalItems / limit) || 1);
    setPage(1);
  }, [filteredReportData, limit]);

  useEffect(() => {
    const startIndex = (page - 1) * limit;
    const sliced = filteredReportData.slice(startIndex, startIndex + limit);
    setPaginatedData(sliced);
  }, [page, limit, filteredReportData]);

  useEffect(() => {
    if (!selectedYear || !selectedMonth) return;

    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;

    const selectedY = Number(selectedYear);
    const selectedM = Number(selectedMonth);

    // CURRENT MONTH
    if (selectedY === currentYear && selectedM === currentMonth) {
      setStartDate(null);
      setEndDate(null);
      setReportData([]);
      setFilteredReportData([]);
      setPaginatedData([]);

      toast.error(
        "The selected month has not ended yet. Monthly data will be available after the month is complete.",
      );
      return;
    }

    // FUTURE MONTH
    if (
      selectedY > currentYear ||
      (selectedY === currentYear && selectedM > currentMonth)
    ) {
      setStartDate(null);
      setEndDate(null);
      setReportData([]);
      setFilteredReportData([]);
      setPaginatedData([]);

      toast.error("No data available for future months.");
      return;
    }

    setMonthRange(selectedYear, selectedMonth);
  }, [selectedYear, selectedMonth]);

  useEffect(() => {
    setLimit(value);
    setPage(1);
  }, [value]);

  useEffect(() => {
    const today = new Date();

    const prevMonthDate = new Date(
      today.getFullYear(),
      today.getMonth() - 1,
      1,
    );

    setSelectedYear(String(prevMonthDate.getFullYear()));
    setSelectedMonth(String(prevMonthDate.getMonth() + 1).padStart(2, "0"));
  }, []);

  return (
    <div className="px-6 py-4 mt-12 bg-[#F8F8F8] min-h-screen">
      <div
        style={{ color: "rgba(0,0,0,0.5)" }}
        className="text-[11px] 2xl:text-sm leading-[18.27px] -tracking-[3%]"
      >
        Dashboard / Manage Reports / Monthly Case Report
      </div>
      <div>
        <h2>Monthly Case Report</h2>
      </div>
      <div className="flex items-center justify-between mt-6">
        <div className="flex flex-wrap items-center gap-4">
          <button className="flex items-center justify-between border border-[#017C57] rounded-full px-5 py-3 bg-[#F8F8F8] min-w-[220px] relative">
            <select
              value={selectedHospitalId}
              onChange={(e) => {
                setSelectedHospitalId(e.target.value);
                setPage(1);
              }}
              className="absolute inset-0 w-full h-full bg-transparent border-none outline-none ring-0 focus:ring-0 focus:outline-none appearance-none cursor-pointer text-[#017C57] text-xs 2xl:text-sm font-medium pl-5 pr-12 opacity-0 z-10"
            >
              <option value="">HOSPITAL NAME</option>
              {hospitalList.map((hosp) => (
                <option key={hosp.id} value={hosp.id}>
                  {hosp.name || hosp.code}
                </option>
              ))}
            </select>

            <span className="text-[#017C57] text-xs 2xl:text-sm font-medium truncate pr-2 flex-1">
              {selectedHospitalId
                ? hospitalList.find((h) => h.id === selectedHospitalId)?.name ||
                  hospitalList.find((h) => h.id === selectedHospitalId)?.code ||
                  "HOSPITAL NAME"
                : "HOSPITAL NAME"}
            </span>

            <img
              src={downArrow}
              alt="dropdown"
              className="w-4 h-4 shrink-0 z-0"
            />
          </button>
          <div className="flex items-center gap-3">
            {/* YEAR */}
            <button className="flex items-center justify-between border border-[#017C57] rounded-full px-5 py-3 bg-[#F8F8F8] min-w-[150px] relative">
              <select
                value={selectedYear}
                onChange={(e) => {
                  setSelectedYear(e.target.value);
                  setPage(1);
                }}
                className="absolute inset-0 w-full h-full bg-transparent border-none outline-none appearance-none cursor-pointer opacity-0"
              >
                <option value="">YEAR</option>
                {Array.from(
                  { length: new Date().getFullYear() - 2019 },
                  (_, i) => 2020 + i,
                ).map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>

              <span className="text-[#017C57] text-xs 2xl:text-sm font-medium">
                {selectedYear || "YEAR"}
              </span>

              <img src={downArrow} className="w-4 h-4" />
            </button>

            {/* MONTH */}
            <button className="flex items-center justify-between border border-[#017C57] rounded-full px-5 py-3 bg-[#F8F8F8] min-w-[150px] relative">
              <select
                value={selectedMonth}
                onChange={(e) => {
                  setSelectedMonth(e.target.value);
                  setPage(1);
                }}
                className="absolute inset-0 w-full h-full bg-transparent border-none outline-none appearance-none cursor-pointer opacity-0 pl-5"
              >
                <option value="">MONTH</option>
                <option value="01">January</option>
                <option value="02">February</option>
                <option value="03">March</option>
                <option value="04">April</option>
                <option value="05">May</option>
                <option value="06">June</option>
                <option value="07">July</option>
                <option value="08">August</option>
                <option value="09">September</option>
                <option value="10">October</option>
                <option value="11">November</option>
                <option value="12">December</option>
              </select>

              <span className="text-[#017C57] text-xs 2xl:text-sm font-medium">
                {selectedMonth
                  ? months.find((m) => m.value === selectedMonth)?.label
                  : "MONTH"}
              </span>

              <img src={downArrow} className="w-4 h-4" />
            </button>
          </div>

          {/* Hospital filter (unchanged) */}

          {/* Case status (unchanged) */}
          <button className="flex items-center justify-between border border-[#017C57] rounded-full px-5 py-3 bg-[#F8F8F8] min-w-[180px] relative">
            <select
              value={caseStage || "all"}
              onChange={(e) => setCaseStage(e.target.value)}
              className="absolute inset-0 w-full h-full bg-transparent border-none outline-none ring-0 focus:ring-0 focus:outline-none appearance-none cursor-pointer text-[#017C57] text-xs 2xl:text-sm font-medium truncate pl-5 pr-12 opacity-0 z-10"
            >
              <option value="all">CASE STATUS</option>
              <option value="Request Admission Initiated">
                Admission Initiated
              </option>
              <option value="Claim Initiated on TMS">
                Claim Initiated on TMS
              </option>
            </select>

            <span className="text-[#017C57] text-xs 2xl:text-sm font-medium truncate pr-2 flex-1">
              {caseStage === "all"
                ? "CASE STATUS"
                : caseStage === "Request Admission Initiated"
                  ? "Admission Initiated"
                  : caseStage === "Claim Initiated on TMS"
                    ? "Claim Initiated on TMS"
                    : "CASE STATUS"}
            </span>

            <img
              src={downArrow}
              alt="dropdown"
              className="w-4 h-4 shrink-0 z-0"
            />
          </button>
        </div>

        <div className="relative shrink-0">
          {/* Download Button */}
          <button
            type="button"
            disabled={downloading}
            onClick={() => {
              if (!downloading) {
                setShowDownloadMenu((prev) => !prev);
              }
            }}
            className="flex items-center justify-center gap-2 border border-none border-[#017C71] rounded-full px-5 py-[13px] bg-[#017C71] text-white text-sm font-medium cursor-pointer min-w-[180px] disabled:cursor-not-allowed"
          >
            {downloading ? (
              <CircularProgress
                size={18}
                thickness={5}
                sx={{
                  color: "#FFFFFF",
                }}
              />
            ) : (
              <>
                <FileDownloadOutlinedIcon
                  sx={{
                    fontSize: 19,
                  }}
                />

                <span>Download Report</span>

                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className={`transition-transform duration-200 ${
                    showDownloadMenu ? "rotate-180" : ""
                  }`}
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </>
            )}
          </button>

          {/* Download Dropdown */}
          {showDownloadMenu && (
            <div className="absolute right-0 top-full mt-0 w-full min-w-[180px] bg-white border border-[#AFAFAF] rounded-b-[10px] rounded-t-none overflow-hidden z-50 shadow-sm">
              <button
                type="button"
                disabled={downloading}
                onClick={() => {
                  setShowDownloadMenu(false);

                  if (filteredReportData?.length > 0) {
                    generatePdf(filteredReportData);
                  } else {
                    toast.info("No data present to generate pdf.");
                  }
                }}
                className="w-full text-left px-4 py-3 text-sm text-[#191919] bg-white hover:bg-[#F8F8F8] border-none disabled:cursor-not-allowed disabled:opacity-50"
              >
                Download PDF
              </button>

              <button
                type="button"
                disabled={downloading}
                onClick={() => {
                  setShowDownloadMenu(false);

                  if (filteredReportData?.length > 0) {
                    generateExcel(filteredReportData);
                  } else {
                    toast.info("No data present to generate excel.");
                  }
                }}
                className="w-full text-left px-4 py-3 text-sm text-[#191919] bg-white hover:bg-[#F8F8F8] border-none disabled:cursor-not-allowed disabled:opacity-50"
              >
                Download Excel
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Rest of component (table, tabs, pagination) remains EXACTLY THE SAME */}
      <div
        id="case-list"
        style={{ background: "#F8F8F8" }}
        className="p-4 2xl:p-6 mt-4 rounded-[24px]"
      >
        {/* ... entire table section unchanged ... */}
        <div className="bg-[white] p-[24px] 2xl:p-[30px] rounded-[15px] mt-[4px]">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-8 text-[11px] 2xl:text-sm leading-[18px] -tracking-[3%] text-[#191919]">
              {[
                { key: "all", label: "ALL" },
                { key: "Admission", label: "ADMISSION" },
                { key: "Enhancement", label: "ENHANCEMENT" },
                { key: "Claim", label: "CLAIM" },
              ].map((tab) => {
                const isActive = stageType === tab.key;
                return (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => {
                      setStageType(tab.key);
                      setPage(1);
                    }}
                    className="flex flex-col items-center gap-[4px] bg-transparent border-none outline-none cursor-pointer"
                  >
                    <span
                      className={`uppercase text-[11px] 2xl:text-xs tracking-[0.8px] ${
                        isActive
                          ? "text-[#017C57] font-semibold"
                          : "text-[#191919]"
                      }`}
                    >
                      {tab.label}
                    </span>
                    <span
                      className={`h-[2px] w-12 rounded-full ${
                        isActive ? "bg-[#017C57]" : "bg-transparent"
                      }`}
                    />
                  </button>
                );
              })}
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-3 border border-solid border-[1px] border-[#017C57] rounded-full box-border px-5 py-3 xl:w-[200px] 2xl:w-[260px]">
                <img
                  src={searchIcon}
                  alt="search-icon"
                  className="w-4 h-4 shrink-0"
                />
                <input
                  type="text"
                  placeholder="Search"
                  className="w-full bg-transparent outline-none border-none text-[#191919] text-xs 2xl:text-sm font-medium placeholder:text-[#191919]"
                  onChange={handleSearch}
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs 2xl:text-sm text-[#555] uppercase tracking-[0.8px]">
                  NUMBER OF CASES
                </span>
                <div className="flex items-center gap-2 px-4 py-2 rounded-full border border-solid border-[#017C57] w-fit cursor-pointer bg-white">
                  <select
                    value={value}
                    onChange={(e) => setValue(Number(e.target.value))}
                    className="bg-transparent outline-none cursor-pointer appearance-none border-none pl-1 pr-6 text-xs 2xl:text-sm"
                  >
                    {[10, 25, 50].map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="black"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="pointer-events-none -ml-4"
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {/* Table header and body - UNCHANGED */}
          <div className="grid grid-cols-13 gap-2 text-[12px] 2xl:text-sm font-semibold 2xl:leading-[21.7px] -tracking-[3%]">
            <div>SNo.</div>
            <div className="col-span-2">Patient Name</div>
            <div>Beneficiary ID</div>
            <div>Registered ID</div>
            <div>Gender/Age</div>
            <div>
              Registered Date{" "}
              <img
                src={UpsideDownArrowIcon}
                alt="upside arrow"
                className="h-4 w-4"
              />
            </div>
            <div>
              Admission Date{" "}
              <img
                src={UpsideDownArrowIcon}
                alt="upside arrow"
                className="h-4 w-4"
              />
            </div>
            <div className="col-span-2 text-center">Current Case Stage</div>
          </div>

          {loading ? (
            <div className="h-[200px] flex justify-center items-center">
              <CircularProgress />
            </div>
          ) : paginatedData?.length > 0 ? (
            paginatedData?.map((reportItem, idx) => {
              const isEven = idx % 2 === 0;
              return (
                <div
                  key={idx}
                  className={`grid grid-cols-13 gap-2 text-[12px] 2xl:text-sm py-1 items-center mt-[8px] 2xl:mt-[10px] first:mt-6 2xl:first:mt-8 ${
                    isEven ? "bg-white" : "bg-[#F9F9F9]"
                  }`}
                >
                  <div>{(page - 1) * limit + idx + 1}</div>
                  <div className="col-span-2">
                    {reportItem?.patientName || "NA"}
                  </div>
                  <div>{reportItem?.beneficiaryNo || "NA"}</div>
                  <div>{reportItem?.pmjayRegistrationId || "NA"}</div>
                  <div>{reportItem?.gender || "NA"}</div>
                  <div>
                    {reportItem?.registeredDate
                      ? new Date(reportItem?.registeredDate).toLocaleString(
                          "en-GB",
                          {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                            second: "2-digit",
                            hour12: false,
                          },
                        )
                      : "NA"}
                  </div>
                  <div>
                    {reportItem?.admissionDate
                      ? new Date(reportItem?.admissionDate).toLocaleDateString(
                          "en-GB",
                          {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                          },
                        )
                      : "NA"}
                  </div>
                  <div className="font-[Lufga] font-medium text-[14px] leading-[18px] tracking-[-0.04em] text-[#1B1B1B] pl-14">
                    {reportItem?.currentStageHsaReportText || "NA"}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="h-[200px] flex justify-center items-center font-semibold text-xl">
              No Case Data Found
            </div>
          )}

          {filteredReportData?.length > 0 && (
            <div className="flex justify-center mt-[12px] 2xl:mt-[15px]">
              <HospitalCustomPaginaion
                page={page}
                setPage={setPage}
                totalPages={totalPages}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CcMonthlyCaseReport;
